import { logger } from '../utils/logger'
import { clearCachedUserInfo } from './mobilePaymentBarApi'

const MOBILE_INIT_DATA_ENDPOINT = '/api/mobile-shop/mobileInitData'
const LOGIN_WITH_TOKEN_ENDPOINT = '/api/mobile-shop/loginWithToken'

// Email/password login endpoint. Override with VITE_LOGIN_ENDPOINT if the
// backend exposes it under a different path.
const LOGIN_ENDPOINT = import.meta.env.VITE_LOGIN_ENDPOINT || '/api/mobile-shop/login'

/** How the current session was established. */
export type AuthMethod = 'token' | 'credentials'

/**
 * Mask an email for logging: j***@example.com
 * Credentials must never reach the logs verbatim, and passwords never at all.
 */
function maskEmail(email: string): string {
    const at = email.indexOf('@')
    if (at <= 0) {
        return '***'
    }
    return `${email[0]}***${email.slice(at)}`
}

// Check URL for token at module load time (before React initializes)
if (typeof window !== 'undefined') {
    const initialParams = new URLSearchParams(window.location.search)
    const initialToken = initialParams.get('mobileToken')
    logger.log('AUTH', 'Module loaded - Initial URL check', {
        hasToken: !!initialToken,
        tokenLength: initialToken?.length || 0,
        tokenPreview: initialToken ? `${initialToken.substring(0, 10)}...` : null,
        fullUrl: window.location.href,
        searchParams: window.location.search,
    })
}

// Cache for mobileInitData to avoid duplicate API calls
let cachedInitData: unknown | null = null
let initDataPromise: Promise<unknown> | null = null

/**
 * Get the base URL for API requests
 * Uses VITE_API_BASE_URL if set, otherwise falls back to window.location.origin
 */
function getApiBaseUrl(): string {
    let baseUrl = window.location.origin
    
    // Use environment variable if set (for different API URLs per environment)
    // Vite will replace import.meta.env.VITE_API_BASE_URL at build time based on .env.{mode} files
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
    if (apiBaseUrl) {
        baseUrl = apiBaseUrl
        // Ensure baseUrl doesn't end with a slash
        baseUrl = baseUrl.replace(/\/$/, '')
    }
    
    return baseUrl
}

/**
 * Authentication service for mobile login token management
 * Handles token extraction, removal from URL, and session establishment
 */
class AuthService {
    private mobileToken: string | null = null
    private isAuthenticated: boolean = false
    private loginPromise: Promise<unknown | null> | null = null
    private loginData: unknown | null = null
    private authMethod: AuthMethod | null = null

    /**
     * POST a login payload and establish the session cookie.
     * Shared by the token and credentials paths so both surface identical
     * error semantics. The payload is never logged - it carries secrets.
     */
    private async postLogin(endpoint: string, payload: Record<string, string>): Promise<void> {
        const url = new URL(endpoint, getApiBaseUrl())

        const response = await fetch(url.toString(), {
            method: 'POST',
            credentials: 'include', // CRITICAL: Include cookies for session
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        })

        if (!response.ok) {
            let errorMessage =
                response.status === 401 || response.status === 403
                    ? 'Mobile authentication required'
                    : `Login failed: ${response.status}`
            try {
                const errorData = await response.json()
                if (errorData?.message) {
                    errorMessage = errorData.message
                }
            } catch {
                // If response is not JSON, keep the default message
            }
            throw new Error(errorMessage)
        }
    }

    /**
     * Extract token from URL and remove it from the URL
     * Safe to call multiple times - will only extract once
     * @returns The extracted token or null if not found
     */
    extractAndRemoveTokenFromUrl(): string | null {
        // Log that function was called
        const urlInfo = {
            currentUrl: window.location.href,
            searchParams: window.location.search,
            hasStoredToken: !!this.mobileToken,
        }
        logger.log('AUTH', 'extractAndRemoveTokenFromUrl called', urlInfo)

        // If we already have a token stored, don't extract again
        if (this.mobileToken) {
            logger.log('AUTH', 'Token already extracted, skipping URL extraction', {
                tokenLength: this.mobileToken.length,
                tokenPreview: `${this.mobileToken.substring(0, 10)}...`,
            })
            return this.mobileToken
        }

        const params = new URLSearchParams(window.location.search)
        const token = params.get('mobileToken')
        
        const tokenInfo = {
            tokenFromUrl: token ? `${token.substring(0, 10)}...` : null,
            tokenLength: token?.length || 0,
            allParams: Array.from(params.entries()),
        }
        logger.log('AUTH', 'Checking URL for token', tokenInfo)

        if (token) {
            // Trim and validate token
            const trimmedToken = token.trim()
            if (trimmedToken.length === 0) {
                logger.warn('AUTH', 'Token found in URL but is empty after trimming')
                return null
            }
            
            this.mobileToken = trimmedToken
            logger.log('AUTH', 'Mobile token found in URL', {
                hasToken: true,
                tokenLength: trimmedToken.length,
                tokenPreview: `${trimmedToken.substring(0, 10)}...`,
                fullUrl: window.location.href,
            })

            // Remove token from URL without page reload
            params.delete('mobileToken')
            const newSearch = params.toString()
            const newUrl = newSearch
                ? `${window.location.pathname}?${newSearch}`
                : window.location.pathname

            window.history.replaceState({}, '', newUrl)
            logger.log('AUTH', 'Token removed from URL', { newUrl, tokenStored: !!this.mobileToken })
        } else {
            logger.log('AUTH', 'No mobile token in URL', {
                hasToken: false,
                currentUrl: window.location.href,
                searchParams: window.location.search,
            })
        }

        return this.mobileToken
    }

    /**
     * Get the current mobile token
     */
    getToken(): string | null {
        return this.mobileToken
    }

    /**
     * Check if we have a token available
     */
    hasToken(): boolean {
        return this.mobileToken !== null
    }

    /**
     * Mark authentication as successful (called after login)
     */
    markAuthenticated(method: AuthMethod = 'token'): void {
        this.isAuthenticated = true
        this.authMethod = method
        // Clear token after successful authentication (no longer needed)
        this.mobileToken = null
        logger.log('AUTH', 'Authentication successful, session cookie set, token cleared', { method })
    }

    /**
     * How the current session was established, or null if not authenticated
     */
    getAuthMethod(): AuthMethod | null {
        return this.authMethod
    }

    /**
     * Check if we're authenticated (have session cookie)
     */
    getIsAuthenticated(): boolean {
        return this.isAuthenticated
    }

    /**
     * Handle authentication error - reset auth state
     */
    handleAuthError(): void {
        this.isAuthenticated = false
        clearCachedUserInfo() // Clear cached user info on auth error
        clearCachedInitData() // Clear cached init data on auth error
        logger.warn('AUTH', 'Authentication error - session may be invalid')
    }

    /**
     * Clear authentication state
     */
    async logout(): Promise<void> {
        await this.waitForLogin()
        const response = await fetch(new URL('/api/mobile-shop/logout', getApiBaseUrl()).toString(), {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
        })
        const result = await response.json()
        if (!response.ok || result.r !== true) {
            throw new Error('Logout failed')
        }
        this.clear()
    }

    clear(): void {
        this.mobileToken = null
        this.isAuthenticated = false
        this.authMethod = null
        clearCachedUserInfo() // Clear cached user info when clearing auth
        clearCachedInitData() // Clear cached init data when clearing auth
        logger.log('AUTH', 'Authentication cleared')
    }

    /**
     * Login with email and password, establishing the session cookie.
     * This is the primary login path; loginWithToken remains supported for
     * deep links opened from the game client.
     *
     * Neither argument is ever logged.
     *
     * @throws Error with a user-presentable message when login is rejected
     */
    async loginWithCredentials(email: string, password: string): Promise<void> {
        const trimmedEmail = email.trim()

        if (!trimmedEmail || !password) {
            throw new Error('Please enter your email and password.')
        }

        // Reuse an in-flight login rather than issuing a second one
        if (this.loginPromise) {
            logger.log('AUTH', 'Login already in progress, waiting...')
            await this.loginPromise
            return
        }

        logger.log('AUTH', 'Attempting login with credentials', {
            email: maskEmail(trimmedEmail),
        })

        this.loginPromise = (async () => {
            try {
                await this.postLogin(LOGIN_ENDPOINT, {
                    email: trimmedEmail,
                    password,
                })

                logger.log('AUTH', 'Credentials login successful, session established')
                this.markAuthenticated('credentials')

                // Drop anything cached while unauthenticated so the next reads
                // reflect the new session rather than a pre-login response.
                clearCachedUserInfo()
                clearCachedInitData()

                // Small delay to ensure the session cookie is committed before
                // dependent requests go out (mirrors the token login path)
                await new Promise((resolve) => setTimeout(resolve, 50))

                return null
            } catch (error) {
                logger.error('AUTH', 'Credentials login failed:', error)
                throw error
            } finally {
                this.loginPromise = null
            }
        })()

        await this.loginPromise
    }

    /**
     * Login with mobile token by calling mobileInitData endpoint
     * This establishes the session cookie for subsequent requests
     * Uses POST request with token in payload
     * Uses shared cache to avoid duplicate calls
     * @returns The response data from mobileInitData, or null if no token
     */
    async loginWithToken(): Promise<unknown | null> {
        logger.log('AUTH', 'loginWithToken called', {
            hasToken: !!this.mobileToken,
            tokenLength: this.mobileToken?.length || 0,
            hasLoginPromise: !!this.loginPromise,
            hasCachedInitData: !!cachedInitData,
            hasInitDataPromise: !!initDataPromise,
        })

        // If login is already in progress, wait for it
        if (this.loginPromise) {
            logger.log('AUTH', 'Login already in progress, waiting...')
            await this.loginPromise
            return null
        }

        if (!this.mobileToken) {
            logger.warn('AUTH', 'No token available for login', {
                mobileToken: this.mobileToken,
                tokenType: typeof this.mobileToken,
            })
            return null
        }

        // Validate token is not empty
        if (this.mobileToken.trim().length === 0) {
            logger.warn('AUTH', 'Token is empty string')
            return null
        }

        // Create and store login promise
        logger.log('AUTH', 'Creating login promise...')
        this.loginPromise = (async () => {
            logger.log('AUTH', 'Login promise executing!')
            try {
                // Log token before making request
                logger.log('AUTH', 'Attempting login with token', {
                    hasToken: !!this.mobileToken,
                    tokenLength: this.mobileToken?.length || 0,
                    tokenPreview: this.mobileToken ? `${this.mobileToken.substring(0, 10)}...` : 'none',
                })
                
                // Call loginWithToken endpoint (separate from mobileInitData)
                logger.log('AUTH', 'Calling loginWithToken endpoint')

                await this.postLogin(LOGIN_WITH_TOKEN_ENDPOINT, {
                    mobileToken: this.mobileToken!,
                })

                // Login successful - session cookie is now set
                // Don't expect data back from login endpoint, just success
                logger.log('AUTH', 'Login successful, session established')

                // Mark as authenticated after successful login
                this.markAuthenticated('token')
                
                // Small delay to ensure session cookie is fully set before allowing other requests
                await new Promise(resolve => setTimeout(resolve, 50))
                logger.log('AUTH', 'Session cookie should be set, login complete')

                // Return null since login endpoint doesn't return data
                return null
            } catch (error) {
                logger.error('AUTH', 'Login failed:', error)
                throw error
            } finally {
                // Clear login promise after completion
                this.loginPromise = null
            }
        })()

        logger.log('AUTH', 'Login promise created, awaiting...')
        const result = await this.loginPromise
        logger.log('AUTH', 'Login promise resolved', { hasResult: !!result })
        return result
    }

    /**
     * Wait for login to complete if it's in progress
     * Useful for ensuring authentication before making API calls
     * Also verifies authentication status after login completes
     */
    async waitForLogin(): Promise<void> {
        if (this.loginPromise) {
            logger.log('AUTH', 'Waiting for login promise to complete...')
            await this.loginPromise
            logger.log('AUTH', 'Login promise completed, verifying authentication...')
            
            // Give a small delay to ensure session cookie is set
            await new Promise(resolve => setTimeout(resolve, 100))
            
            // Verify we're authenticated after login
            if (!this.isAuthenticated) {
                logger.warn('AUTH', 'Login completed but not marked as authenticated')
                // Try to mark as authenticated if we have no token (token was cleared after login)
                if (!this.mobileToken) {
                    this.isAuthenticated = true
                    logger.log('AUTH', 'Marked as authenticated after login')
                }
            }
        }
    }

    /**
     * Get the data from the login response (mobileInitData)
     * Returns null if login hasn't completed or wasn't performed
     */
    getLoginData(): unknown | null {
        return this.loginData
    }
}

// Export singleton instance
export const authService = new AuthService()

/**
 * Fetch mobileInitData with caching support
 * Can be used for both POST (with token) and GET (with session) requests
 */
export const fetchMobileInitData = async (options?: { 
    method?: 'GET' | 'POST'
    body?: string
}): Promise<unknown> => {
    logger.log('AUTH', 'fetchMobileInitData called', {
        method: options?.method || 'GET',
        hasBody: !!options?.body,
        hasCachedData: !!cachedInitData,
        hasInitDataPromise: !!initDataPromise,
    })
    
    // Check cache first
    if (cachedInitData) {
        logger.log('AUTH', 'Using cached mobileInitData')
        return cachedInitData
    }

    // If there's already a request in progress, wait for it
    if (initDataPromise) {
        logger.log('AUTH', 'Waiting for existing mobileInitData request')
        return await initDataPromise
    }

    // Create new request
    logger.log('AUTH', 'Creating new initDataPromise...')
    initDataPromise = (async () => {
        logger.log('AUTH', 'initDataPromise executing!')
        try {
            const baseUrl = getApiBaseUrl()
            const url = new URL(MOBILE_INIT_DATA_ENDPOINT, baseUrl)

            logger.log('AUTH', 'Fetching mobileInitData', {
                method: options?.method || 'GET',
                url: url.toString(),
                hasBody: !!options?.body,
                bodyPreview: options?.body ? options.body.substring(0, 100) : 'none',
            })

            logger.log('AUTH', 'About to call fetch...')
            const response = await fetch(url.toString(), {
                method: options?.method || 'GET',
                credentials: 'include', // CRITICAL: Include cookies for session
                headers: {
                    'Content-Type': 'application/json',
                },
                body: options?.body,
            })
            logger.log('AUTH', 'Fetch completed', { status: response.status, ok: response.ok })

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    let errorMessage = 'Mobile authentication required'
                    try {
                        const errorData = await response.json()
                        if (errorData.message) {
                            errorMessage = errorData.message
                        }
                    } catch {
                        // If response is not JSON, use default message
                    }
                    throw new Error(errorMessage)
                }
                throw new Error(`Failed to fetch mobileInitData: ${response.status}`)
            }

            const json = await response.json()
            if (json.r !== true || !json.data) throw new Error('Invalid shop response')
            const data = (json as { r: boolean; data: unknown }).data

            // Cache the result
            cachedInitData = data
            logger.log('AUTH', 'mobileInitData cached')

            return data
        } catch (error) {
            logger.error('AUTH', 'Failed to fetch mobileInitData:', error)
            // Clear promise on error so it can be retried
            initDataPromise = null
            throw error
        }
    })()

    return await initDataPromise
}

/**
 * Clear cached mobileInitData (useful when auth state changes)
 */
export const clearCachedInitData = (): void => {
    cachedInitData = null
    initDataPromise = null
    logger.log('AUTH', 'Cleared cached mobileInitData')
}

/**
 * Get cached mobileInitData if available
 */
export const getCachedInitData = (): unknown | null => {
    return cachedInitData
}

/**
 * Make authenticated fetch request with session cookie
 * All requests after login use only the session cookie (no token)
 * Waits for login to complete if a login is in progress
 */
export async function authenticatedFetch(
    endpoint: string,
    options: RequestInit = {}
): Promise<Response> {
    // Wait for login to complete if it's in progress
    // This ensures session cookie is set before making authenticated requests
    await authService.waitForLogin()

    // Handle absolute URLs (e.g., external checkout URLs)
    const url = endpoint.startsWith('http://') || endpoint.startsWith('https://')
        ? endpoint
        : new URL(endpoint, getApiBaseUrl()).toString()

    const fetchOptions: RequestInit = {
        ...options,
        credentials: 'include', // CRITICAL: Include cookies for session
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    }

    const response = await fetch(url, fetchOptions)

    // Handle authentication errors
    if (response.status === 401 || response.status === 403) {
        authService.handleAuthError()

        let errorMessage = 'Mobile authentication required'
        try {
            const errorData = await response.json()
            if (errorData.message) {
                errorMessage = errorData.message
            }
        } catch {
            // If response is not JSON, use default message
        }

        throw new Error(errorMessage)
    }

    return response
}

